import { AuthEventBus } from './AuthEventBus';
import { OidcClient } from '@axa-fr/oidc-client';
import { batch, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { KeycloakConfig } from '@thinkalpha/ui-types/types/schemas/OAuthConfigSchema';
import { jwtDecode } from 'jwt-decode';
import { injectable, inject } from 'src/features/ioc';
import type {
    ManagedOAuth2Service,
    OAuthFlowState,
    LoginCallbackResult,
    AuthEventCallback,
    EnsureValidSessionResult,
} from 'src/services/OAuth2Service';
import type { OAuth2StorageService } from 'src/services/OAuth2StorageService';
import type { OidcLocationService } from 'src/services/OidcLocationService';
import type { SessionService, SessionState, TokenClaims } from 'src/services/SessionService';
import type { SyncStorageService } from 'src/services/SyncStorageService';
import { container as staticContainer } from 'src/StaticContainer';
import type { IsomorphicBindings } from 'src/types/bindings';

const SESSION_RESTORED_KEY = 'oidc_session_restored';
const SESSION_EXPIRED_KEY = 'platform-ui-session-expired';
const SESSION_LOGGED_OUT_KEY = 'platform-ui-session-logged-out';
const SESSION_REFRESH_LOCK = 'platform-ui-session-refresh';

type RefreshTrigger = 'bootstrap' | 'expiry' | 'server-rejected';
type SessionExpirationSource = 'cross-tab-signal' | 'local' | 'oidc-shared-session';

interface RefreshAttempt {
    readonly abortController: AbortController;
    readonly generation: number;
    readonly promise: Promise<SessionState>;
    readonly sourceToken: string | null;
}

@injectable()
export class OidcClientOAuth2ServiceImpl implements ManagedOAuth2Service, SessionService {
    type = 'managed' as const;

    readonly #accessToken: Signal<string | null> = signal(null);
    #authEventBus: AuthEventBus;
    #config: KeycloakConfig;
    #log: IsomorphicBindings['Logger'];
    #lastLogoutSignalGeneration = -1;
    #oidcClient: OidcClient | null = null;
    #oidcLocation: OidcLocationService;
    #oidcRefreshGeneration: number | null = null;
    #remoteLogoutTeardownInProgress = false;
    #refreshAttempt: RefreshAttempt | null = null;
    #replacementAwaitingValidation: string | null = null;
    #sessionGeneration = 0;
    readonly #state: Signal<SessionState> = signal({ phase: 'authenticating' });
    #syncStorage: SyncStorageService;

    get accessToken(): ReadonlySignal<string | null> {
        return this.#accessToken;
    }

    get state(): ReadonlySignal<SessionState> {
        return this.#state;
    }

    constructor(
        @inject('ConfigService') protected readonly config: IsomorphicBindings['ConfigService'],
        @inject('Logger') protected readonly log: IsomorphicBindings['Logger'],
        @inject('OAuth2StorageService') protected readonly oAuth2Storage: OAuth2StorageService,
        @inject('OidcLocationService') oidcLocation: OidcLocationService,
        @inject('SyncStorageService') syncStorage: SyncStorageService,
    ) {
        this.#log = log.getSubLogger(
            { name: 'features:oauth2:lib:OidcClientOAuth2ServiceImpl' },
            { component: 'auth' },
        );
        this.#config = config.oAuthConfig as KeycloakConfig;
        this.#authEventBus = new AuthEventBus();
        this.#oidcLocation = oidcLocation;
        this.#syncStorage = syncStorage;

        this.#initializeOidcClient();
        this.#startCrossTabSessionMonitor();
    }

    #initializeOidcClient(): void {
        const { keycloakURL, tenantID, applicationID, callbackURL } = this.#config;

        // Build the authority URL from Keycloak config
        const authority = new URL(`${tenantID}`, keycloakURL).toString();

        // Construct silent callback URL - must be on same origin as main app
        const silentCallbackUrl = new URL('/silent-callback.html', callbackURL).toString();

        // TODO: Make scope configurable
        const configuration = {
            client_id: applicationID,
            redirect_uri: callbackURL,
            authority,
            scope: 'openid profile offline_access', // offline_access for refresh tokens
            // Use injectable storage instead of sessionStorage (default) so tokens are shared
            // across tabs. This ensures logout clears tokens for all tabs immediately.
            // On web this wraps localStorage; on native it uses file-based Persisted storage.
            storage: this.#syncStorage as Storage,
            // TODO: Make these configurable
            response_type: 'code',
            loadUserInfo: false, // We handle userInfo ourselves

            // Automatic silent token renewal configuration
            // When enabled, the OIDC client will automatically refresh tokens before they expire
            // using a hidden iframe that loads silent_redirect_uri.
            // Disabled in Node.js (Electron main process) where there is no DOM/iframe support.
            automaticSilentRenew: typeof document !== 'undefined',
            silent_redirect_uri: silentCallbackUrl,
            // Refresh tokens before they expire to prevent gaps in authorization
            // Refresh with enough margin for normal network and scheduling jitter.
            refresh_time_before_tokens_expiration_in_second: 60,
            // Trigger renewal when access token or ID token becomes invalid
            token_renew_mode: 'access_token_or_id_token_invalid',

            // Keep explicit login flow (no automatic silent signin on app load)
            automaticSilentSignin: false,

            // Revoke tokens server-side during logout to prevent other tabs from
            // using their cached refresh tokens to create new sessions
            logout_tokens_to_invalidate: ['access_token', 'refresh_token'] as ('access_token' | 'refresh_token')[],
        };

        if (TRACE) {
            this.#log.trace('Initializing OIDC client with configuration', { configuration });
        }

        this.#oidcClient = OidcClient.getOrCreate(() => fetch, this.#oidcLocation)(configuration);

        // Subscribe to OIDC client events
        this.#oidcClient.subscribeEvents((eventName: string, data: any) => {
            this.#handleOidcEvent(eventName, data);
        });
    }

    #decodeClaims(accessToken: string): TokenClaims {
        try {
            return jwtDecode<TokenClaims>(accessToken);
        } catch {
            // OAuth permits opaque access tokens. OIDC has already validated the token;
            // there are simply no client-readable claims to expose in that case.
            return {};
        }
    }

    #setAuthenticated(tokens: LoginCallbackResult['tokens']): void {
        const accessToken = tokens.accessToken;
        const previousToken = this.#accessToken.value;
        const previousPhase = this.#state.value.phase;

        batch(() => {
            this.#accessToken.value = accessToken;
            this.#state.value = {
                phase: 'authenticated',
                claims: this.#decodeClaims(accessToken),
            };
        });

        if (this.#replacementAwaitingValidation && this.#replacementAwaitingValidation !== accessToken) {
            this.#replacementAwaitingValidation = null;
        }

        if (previousToken === accessToken && previousPhase === 'authenticated') {
            return;
        }

        this.#authEventBus.emit({
            type: 'TOKEN_REFRESHED',
            payload: { tokens },
        });
    }

    #setExpired(
        reason: Extract<SessionState, { phase: 'expired' }>['reason'],
        source: SessionExpirationSource = 'local',
    ): SessionState {
        const previousState = this.#state.value;
        if (previousState.phase === 'expired' && previousState.reason === 'logout') {
            return previousState;
        }
        if (previousState.phase === 'expired' && previousState.reason === reason) {
            return previousState;
        }

        // Expiration is authoritative for the current session generation. It
        // must cancel local refresh/bootstrap work so a stale completion cannot
        // replace the overlay with tokens invalidated by another tab.
        this.#oidcRefreshGeneration ??= this.#sessionGeneration;
        this.#advanceSessionGeneration('session-expired');
        this.#replacementAwaitingValidation = null;
        batch(() => {
            this.#accessToken.value = null;
            this.#state.value = { phase: 'expired', reason };
        });

        this.#authEventBus.emit({ type: 'SESSION_EXPIRED' });
        try {
            staticContainer.getOptional('AnalyticsService')?.track({ type: 'session_expired' });
        } catch {
            // Analytics should never break the app
        }

        this.#log.warn('Session expired', { reason, source });
        if (source !== 'cross-tab-signal') {
            this.#signalCrossTabSessionExpiration();
        }
        return this.#state.value;
    }

    #setLoggedOut(): void {
        if (this.#state.value.phase === 'expired' && this.#state.value.reason === 'logout') {
            return;
        }

        this.#advanceSessionGeneration('logout');
        this.#replacementAwaitingValidation = null;
        batch(() => {
            this.#accessToken.value = null;
            this.#state.value = { phase: 'expired', reason: 'logout' };
        });
        this.#authEventBus.emit({ type: 'LOGOUT' });
    }

    #advanceSessionGeneration(reason: 'interactive-login' | 'logout' | 'session-expired'): void {
        this.#sessionGeneration++;

        const refreshAttempt = this.#refreshAttempt;
        if (!refreshAttempt || refreshAttempt.abortController.signal.aborted) {
            return;
        }

        if (TRACE) {
            this.#log.trace('Cancelling refresh for a superseded session', {
                attemptGeneration: refreshAttempt.generation,
                reason,
                sessionGeneration: this.#sessionGeneration,
            });
        }
        refreshAttempt.abortController.abort();
    }

    #isRefreshSuperseded(generation: number, signal: AbortSignal): boolean {
        return signal.aborted || generation !== this.#sessionGeneration;
    }

    #handleOidcEvent(eventName: string, data: any): void {
        switch (eventName) {
            case 'token_timer':
            case 'refreshTokensAsync_end':
            case 'tryKeepExistingSessionAsync_begin':
            case 'tryKeepExistingSessionAsync_end':
                // Internal OIDC client events - ignore silently
                break;

            case 'refreshTokensAsync_begin':
                // Automatic renewal is owned by the OIDC client, so its begin
                // event is the only point where we can associate the eventual
                // outcome with the session that initiated it.
                // Retries emit another begin event, but they still belong to
                // the generation that started the renewal sequence.
                // A new initial attempt must replace state left behind when an
                // earlier sequence stopped after a recoverable silent error.
                if (data?.tryNumber === 0 || this.#oidcRefreshGeneration === null) {
                    this.#oidcRefreshGeneration = this.#sessionGeneration;
                }
                break;

            case 'refreshTokensAsync_silent_error':
                // This event is diagnostic, not terminal. The OIDC client may
                // retry after network and 5xx failures. A terminal session loss
                // is reported separately through refreshTokensAsync_error.
                if (TRACE) {
                    this.#log.trace('OIDC token refresh encountered a recoverable error', {
                        message: typeof data?.message === 'string' ? data.message : undefined,
                        status: typeof data?.tokenResponse?.status === 'number' ? data.tokenResponse.status : undefined,
                    });
                }
                break;

            case 'refreshTokensAsync_error': {
                const refreshGeneration = this.#oidcRefreshGeneration ?? this.#sessionGeneration;
                this.#oidcRefreshGeneration = null;

                if (
                    refreshGeneration !== this.#sessionGeneration ||
                    (this.#state.value.phase === 'expired' && this.#state.value.reason === 'logout')
                ) {
                    if (TRACE) {
                        this.#log.trace('Ignoring terminal token refresh failure from a stale session', {
                            refreshGeneration,
                            sessionGeneration: this.#sessionGeneration,
                        });
                    }
                    break;
                }

                this.#log.warn('OIDC client reported terminal token refresh failure', {
                    message: typeof data?.message === 'string' ? data.message : undefined,
                });
                this.#setExpired('refresh-failed');
                break;
            }

            case 'token_renewed': {
                const refreshGeneration = this.#oidcRefreshGeneration;
                this.#oidcRefreshGeneration = null;

                if (this.#state.value.phase === 'expired' && this.#state.value.reason === 'logout') {
                    if (TRACE) {
                        this.#log.trace('Ignoring token renewal after logout');
                    }
                    break;
                }

                if (refreshGeneration !== null && refreshGeneration !== this.#sessionGeneration) {
                    if (TRACE) {
                        this.#log.trace('Ignoring token renewed by a stale automatic refresh', {
                            refreshGeneration,
                            sessionGeneration: this.#sessionGeneration,
                        });
                    }
                    break;
                }

                const refreshAttempt = this.#refreshAttempt;
                if (
                    refreshAttempt &&
                    this.#isRefreshSuperseded(refreshAttempt.generation, refreshAttempt.abortController.signal)
                ) {
                    if (TRACE) {
                        this.#log.trace('Ignoring token renewed by a stale session refresh', {
                            attemptGeneration: refreshAttempt.generation,
                            sessionGeneration: this.#sessionGeneration,
                        });
                    }
                    break;
                }

                // Read tokens from the OIDC client's canonical source, not from event data
                // The event data structure varies, but this.#oidcClient.tokens is always up-to-date
                const tokens = this.#oidcClient?.tokens;
                const accessToken = tokens?.accessToken;
                if (TRACE) {
                    this.#log.trace('token_renewed: Reading tokens from OIDC client', {
                        hasOidcClient: !!this.#oidcClient,
                        hasTokens: !!tokens,
                        accessTokenLength: accessToken?.length ?? 0,
                        eventDataKeys: Object.keys(data || {}),
                    });
                }
                if (accessToken) {
                    this.#setAuthenticated({
                        accessToken,
                        idToken: tokens?.idToken,
                        refreshToken: tokens?.refreshToken,
                    });
                } else {
                    this.#log.warn('token_renewed event received but no access token available');
                }
                break;
            }

            case 'token_expired': {
                const generation = this.#sessionGeneration;
                void this.#refresh('expiry').catch((error) => {
                    this.#log.error('Session expiry refresh could not be coordinated', { error });
                    if (
                        generation === this.#sessionGeneration &&
                        !(this.#state.value.phase === 'expired' && this.#state.value.reason === 'logout')
                    ) {
                        this.#setExpired('refresh-failed');
                    }
                });
                break;
            }

            case 'logout_from_same_tab':
                this.#setLoggedOut();
                if (!this.#remoteLogoutTeardownInProgress) {
                    this.#signalCrossTabLogout();
                }
                break;

            case 'logout_from_another_tab':
            case 'session_terminated':
                // AXA emits logout_from_another_tab both for explicit logout and
                // when another tab's terminal refresh clears shared tokens. Our
                // explicit storage signal is the authoritative logout intent;
                // without it, preserve the recoverable reauthentication flow.
                this.#setExpired('refresh-failed', 'oidc-shared-session');
                break;

            default:
                if (TRACE) {
                    this.#log.trace('Unhandled OIDC event', { eventName, data });
                }
        }
    }

    #startCrossTabSessionMonitor(): void {
        if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
            window.addEventListener('storage', (e) => {
                if (e.key === SESSION_LOGGED_OUT_KEY && e.newValue) {
                    this.#handleCrossTabLogout();
                    return;
                }

                if (e.key === SESSION_EXPIRED_KEY && e.newValue) {
                    this.#setExpired('refresh-failed', 'cross-tab-signal');
                    return;
                }

                if (e.key === SESSION_RESTORED_KEY && e.newValue) {
                    if (this.#state.value.phase === 'expired' && this.#state.value.reason === 'logout') {
                        return;
                    }

                    const generation = this.#sessionGeneration;
                    void this.#restoreCrossTabSession(generation).catch((error) => {
                        this.#log.error('Cross-tab session restoration failed unexpectedly', { error });
                    });
                }
            });
        }
    }

    async #restoreCrossTabSession(generation: number): Promise<void> {
        const oidcClient = this.#oidcClient;
        if (!oidcClient) {
            return;
        }

        this.#log.info('Cross-tab session restoration started');

        try {
            await oidcClient.tryKeepExistingSessionAsync();
        } catch (error) {
            if (TRACE) {
                this.#log.trace('Could not load the shared session during cross-tab restoration', { error });
            }
        }

        if (
            generation !== this.#sessionGeneration ||
            (this.#state.value.phase === 'expired' && this.#state.value.reason === 'logout')
        ) {
            return;
        }

        const tokens = oidcClient.tokens;
        if (tokens?.accessToken && !this.#isAccessTokenExpired(tokens.accessToken)) {
            const sessionChanged =
                this.#state.value.phase !== 'authenticated' || this.#accessToken.value !== tokens.accessToken;
            this.#setAuthenticated({
                accessToken: tokens.accessToken,
                idToken: tokens.idToken,
                refreshToken: tokens.refreshToken,
            });
            if (sessionChanged) {
                this.#authEventBus.emit({ type: 'SESSION_RESTORED' });
                this.#log.info('Cross-tab session restoration succeeded', { source: 'shared-session' });
            }
            return;
        }

        const state = await this.#refresh('bootstrap');
        if (
            generation !== this.#sessionGeneration ||
            (this.#state.value.phase === 'expired' && this.#state.value.reason === 'logout')
        ) {
            return;
        }

        if (state.phase === 'authenticated' && this.#accessToken.value) {
            this.#authEventBus.emit({ type: 'SESSION_RESTORED' });
            this.#log.info('Cross-tab session restoration succeeded', { source: 'refresh' });
            return;
        }

        this.#log.warn('Cross-tab session restoration did not produce an authenticated session');
    }

    #handleCrossTabLogout(): void {
        this.#setLoggedOut();

        if (this.#remoteLogoutTeardownInProgress) {
            return;
        }
        this.#remoteLogoutTeardownInProgress = true;

        // Shared storage clearing does not stop this tab's automatic renewal
        // timer or clear the OIDC client's in-memory tokens.
        // Navigation is synchronous with the authoritative clear so the
        // authenticated tree cannot continue issuing requests while teardown
        // performs discovery or revocation work.
        void this.#tearDownOidcWithoutRedirect()
            .catch((error: unknown) => {
                this.#log.error('Failed to tear down OIDC state after cross-tab logout', { error });
            })
            .finally(() => {
                this.#remoteLogoutTeardownInProgress = false;
            });
        this.#oidcLocation.open(this.#config.loggedOutURL);
    }

    #tearDownOidcWithoutRedirect(): Promise<void> {
        return this.#oidcClient?.logoutAsync(null, { 'no_reload:oidc': 'true' }) ?? Promise.resolve();
    }

    #signalCrossTabLogout(): void {
        if (typeof window === 'undefined' || this.#lastLogoutSignalGeneration === this.#sessionGeneration) {
            return;
        }

        try {
            // Storage events only fire in other tabs when the value changes.
            const previousSignal = Number(this.#syncStorage.getItem(SESSION_LOGGED_OUT_KEY));
            const nextSignal =
                Number.isSafeInteger(previousSignal) && previousSignal < Number.MAX_SAFE_INTEGER
                    ? Math.max(Date.now(), previousSignal + 1)
                    : Date.now();
            this.#syncStorage.setItem(SESSION_LOGGED_OUT_KEY, String(nextSignal));
            this.#lastLogoutSignalGeneration = this.#sessionGeneration;
        } catch (error) {
            this.#log.error('Failed to signal logout to other tabs', { error });
        }
    }

    #signalCrossTabSessionExpiration(): void {
        if (typeof window === 'undefined') {
            return;
        }

        try {
            this.#syncStorage.setItem(SESSION_EXPIRED_KEY, Date.now().toString());
            this.#syncStorage.removeItem(SESSION_EXPIRED_KEY);
        } catch (error) {
            this.#log.warn('Failed to signal session expiration to other tabs', { error });
        }
    }

    #signalCrossTabSessionRestoration(): void {
        if (typeof window === 'undefined') {
            return;
        }

        try {
            this.#syncStorage.setItem(SESSION_RESTORED_KEY, Date.now().toString());
            this.#syncStorage.removeItem(SESSION_RESTORED_KEY);
        } catch (error) {
            this.#log.warn('Failed to signal session restoration to other tabs', { error });
        }
    }

    /**
     * Check whether an access token JWT is expired (or will expire within a
     * small grace window). Returns false for non-JWT tokens or tokens without
     * an `exp` claim so they proceed to server-side validation.
     */
    #isAccessTokenExpired(accessToken: string): boolean {
        try {
            const parts = accessToken.split('.');
            if (parts.length !== 3) return false; // Not a JWT
            const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            const payload: { exp?: number } = JSON.parse(atob(base64));
            if (typeof payload.exp !== 'number') return false;
            // 5-second grace window to avoid edge-case races between check and use
            return payload.exp * 1000 <= Date.now() + 5000;
        } catch {
            return false;
        }
    }

    /**
     * Remove only OIDC login flow state keys from sync storage, preserving
     * token storage and any non-OIDC data. These are the keys that can become
     * stale and cause "State not valid" errors on subsequent login attempts.
     */
    #clearOidcLoginFlowKeys(): void {
        const loginFlowPrefixes = [
            'oidc.state.',
            'oidc.nonce.',
            'oidc.code_verifier.',
            'oidc.login.',
            'oidc.session_state.',
        ];

        const keysToRemove: string[] = [];
        for (let i = 0; i < this.#syncStorage.length; i++) {
            const key = this.#syncStorage.key(i);
            if (key && loginFlowPrefixes.some((prefix) => key.startsWith(prefix))) {
                keysToRemove.push(key);
            }
        }

        for (const key of keysToRemove) {
            this.#syncStorage.removeItem(key);
        }

        if (TRACE) {
            this.#log.trace('Cleared OIDC login flow keys', { removedKeys: keysToRemove });
        }
    }

    async startOauth(state: OAuthFlowState = {}): Promise<void> {
        if (TRACE) {
            this.#log.trace('Starting OAuth flow', { state });
        }

        if (!this.#oidcClient) {
            throw new Error('OIDC client not initialized');
        }

        // Check if a login flow is already in progress
        if (this.oAuth2Storage.isLoginFlowInProgress()) {
            this.#log.warn('Attempt to start multiple OAuth flows - ignoring duplicate request', { state });
            return;
        }

        // Store the OAuth state
        await this.oAuth2Storage.storeOAuthState(state);
        this.#advanceSessionGeneration('interactive-login');
        batch(() => {
            this.#accessToken.value = null;
            this.#state.value = { phase: 'authenticating' };
        });

        try {
            staticContainer.getOptional('AnalyticsService')?.track({ type: 'login_initiated' });
        } catch {
            // Analytics should never break the app
        }

        try {
            await this.#oidcClient.loginAsync(undefined, state.extras);
        } catch (error) {
            this.#log.error('Failed to start OAuth flow', { error, state });
            throw error;
        }
    }

    async loginCallback(queryString: string): Promise<LoginCallbackResult> {
        if (TRACE) {
            this.#log.trace('Processing login callback', { queryString });
        }

        if (!this.#oidcClient) {
            throw new Error('OIDC client not initialized');
        }

        this.#oidcLocation.setCurrentHref?.(queryString);

        try {
            const callbackResult = await this.#oidcClient.loginCallbackAsync();
            const state = await this.oAuth2Storage.getOAuthState();

            const tokens = this.#oidcClient.tokens;

            if (TRACE) {
                this.#log.trace('Login callback successful', { callbackResult, tokens });
            }

            try {
                staticContainer.getOptional('AnalyticsService')?.track({ type: 'login_success' });
            } catch {
                // Analytics should never break the app
            }

            const result: LoginCallbackResult = {
                state,
                tokens: {
                    accessToken: tokens.accessToken,
                    idToken: tokens.idToken,
                    refreshToken: tokens.refreshToken,
                },
            };
            this.#setAuthenticated(result.tokens);
            this.#signalCrossTabSessionRestoration();
            return result;
        } catch (error) {
            // Detect OAuth state mismatch - this happens when sync-storage contains
            // stale OIDC state from a previous session. Clear the corrupted state so
            // a retry of the login flow can succeed.
            if (error instanceof Error && error.message.includes('State not valid')) {
                this.#log.warn('OAuth state mismatch detected - clearing stale auth state from storage');
                try {
                    this.#clearOidcLoginFlowKeys();
                    await this.oAuth2Storage.clearOAuthData();
                } catch (cleanupError) {
                    this.#log.error('Failed to clear stale auth state during state mismatch recovery', {
                        error: cleanupError,
                    });
                }
            }

            this.#log.error('Login callback failed', { error, queryString });
            try {
                staticContainer.getOptional('AnalyticsService')?.track({
                    type: 'login_failure',
                    error: error instanceof Error ? error.message : String(error),
                });
            } catch {
                // Analytics should never break the app
            }
            throw error;
        }
    }

    async logout(options?: { noRedirect?: boolean }): Promise<void> {
        if (TRACE) {
            this.#log.trace('Starting logout', { options });
        }

        if (!this.#oidcClient) {
            this.#log.warn('OIDC client not initialized during logout');
            return;
        }

        try {
            // Check if we have tokens before calling logoutAsync.
            // If tokens were already cleared (e.g., by another tab logging out),
            // the OIDC client's logoutAsync would use an empty id_token_hint=""
            // (fallback value when e.tokens is null), which Keycloak rejects.
            // In this case, tear down without calling the end-session endpoint.
            const hasTokens = Boolean(this.#oidcClient.tokens?.idToken);

            if (hasTokens) {
                // Build extras for logoutAsync
                // The library checks for "no_reload:oidc" === "true" to skip the redirect
                const extras = options?.noRedirect ? { 'no_reload:oidc': 'true' } : undefined;

                await this.#oidcClient.logoutAsync(this.#config.loggedOutURL, extras);
            } else {
                this.#log.info('No tokens available, tearing down OIDC without an end-session redirect');
                await this.#tearDownOidcWithoutRedirect();
            }

            // Clear stored OAuth data
            await this.oAuth2Storage.clearOAuthData();
            this.#setLoggedOut();
            this.#signalCrossTabLogout();

            // logoutAsync owns the redirect when tokens are available.
            if (!hasTokens && !options?.noRedirect) {
                this.#oidcLocation.open(this.#config.loggedOutURL);
            }

            this.#log.info('Logout completed successfully');
        } catch (error) {
            this.#log.error('Logout failed', { error });
            throw error;
        }
    }

    async getValidToken(): Promise<string> {
        if (!this.#oidcClient) {
            throw new Error('OIDC client not initialized');
        }

        try {
            const validToken = await this.#oidcClient.getValidTokenAsync();
            return (validToken as any).access_token || (validToken as any).accessToken || String(validToken);
        } catch (error) {
            this.#log.error('Failed to get valid token', { error });
            throw error;
        }
    }

    async handleUnauthorized(): Promise<{ success: boolean; token?: string }> {
        const state = await this.refresh();
        const token = this.#accessToken.value;
        return state.phase === 'authenticated' && token ? { success: true, token } : { success: false };
    }

    refresh(rejectedAccessToken?: string): Promise<SessionState> {
        if (this.#state.value.phase === 'expired' && this.#state.value.reason === 'logout') {
            return Promise.resolve(this.#state.value);
        }

        const currentToken = this.#accessToken.value ?? this.#oidcClient?.tokens?.accessToken ?? null;
        if (rejectedAccessToken !== undefined && rejectedAccessToken !== currentToken) {
            if (TRACE) {
                this.#log.trace('Ignoring a stale access-token rejection', {
                    hasCurrentToken: currentToken !== null,
                });
            }
            return Promise.resolve(this.#state.value);
        }

        return this.#refresh('server-rejected', rejectedAccessToken);
    }

    #refresh(trigger: RefreshTrigger, reportedRejectedToken?: string): Promise<SessionState> {
        if (this.#state.value.phase === 'expired' && this.#state.value.reason === 'logout') {
            return Promise.resolve(this.#state.value);
        }

        const refreshAttempt = this.#refreshAttempt;
        if (refreshAttempt) {
            if (this.#isRefreshSuperseded(refreshAttempt.generation, refreshAttempt.abortController.signal)) {
                const retry = () =>
                    trigger === 'server-rejected'
                        ? this.refresh(reportedRejectedToken)
                        : this.#refresh(trigger, reportedRejectedToken);
                return refreshAttempt.promise.then(
                    () => retry(),
                    () => retry(),
                );
            }

            if (
                trigger === 'server-rejected' &&
                reportedRejectedToken !== undefined &&
                reportedRejectedToken !== refreshAttempt.sourceToken
            ) {
                return refreshAttempt.promise.then(() => this.refresh(reportedRejectedToken));
            }
            return refreshAttempt.promise;
        }

        const sourceToken =
            reportedRejectedToken ?? this.#accessToken.value ?? this.#oidcClient?.tokens?.accessToken ?? null;
        if (
            trigger === 'server-rejected' &&
            sourceToken !== null &&
            sourceToken === this.#replacementAwaitingValidation
        ) {
            return Promise.resolve(this.#setExpired('server-rejected'));
        }

        const generation = this.#sessionGeneration;
        const abortController = new AbortController();
        const promise = Promise.resolve()
            .then(() => this.#coordinateRefresh(trigger, sourceToken, generation, abortController.signal))
            .finally(() => {
                if (this.#refreshAttempt?.abortController === abortController) {
                    this.#refreshAttempt = null;
                }
            });
        const attempt: RefreshAttempt = {
            abortController,
            generation,
            promise,
            sourceToken,
        };
        this.#refreshAttempt = attempt;
        return attempt.promise;
    }

    async #coordinateRefresh(
        trigger: RefreshTrigger,
        sourceToken: string | null,
        generation: number,
        signal: AbortSignal,
    ): Promise<SessionState> {
        const refresh = async () => {
            if (this.#isRefreshSuperseded(generation, signal)) {
                return this.#state.value;
            }

            if (sourceToken && this.#oidcClient) {
                try {
                    const hasStoredSession = await this.#oidcClient.tryKeepExistingSessionAsync();
                    const tokens = this.#oidcClient.tokens;
                    if (this.#isRefreshSuperseded(generation, signal)) {
                        return this.#state.value;
                    }
                    if (
                        hasStoredSession &&
                        tokens?.accessToken &&
                        tokens.accessToken !== sourceToken &&
                        !this.#isAccessTokenExpired(tokens.accessToken)
                    ) {
                        this.#setAuthenticated({
                            accessToken: tokens.accessToken,
                            idToken: tokens.idToken,
                            refreshToken: tokens.refreshToken,
                        });
                        if (trigger === 'server-rejected') {
                            this.#replacementAwaitingValidation = tokens.accessToken;
                        }
                        this.#log.info('Session refresh adopted from another tab', { trigger });
                        return this.#state.value;
                    }
                } catch (error) {
                    if (this.#isRefreshSuperseded(generation, signal)) {
                        return this.#state.value;
                    }
                    if (TRACE) {
                        this.#log.trace('Could not adopt a session refreshed by another tab', { error, trigger });
                    }
                }
            }

            if (this.#isRefreshSuperseded(generation, signal)) {
                return this.#state.value;
            }
            return this.#performRefresh(trigger, sourceToken, generation, signal);
        };

        if (typeof navigator === 'undefined' || !navigator.locks) {
            return refresh();
        }

        try {
            return await navigator.locks.request(SESSION_REFRESH_LOCK, { signal }, refresh);
        } catch (error) {
            if (this.#isRefreshSuperseded(generation, signal)) {
                if (TRACE) {
                    this.#log.trace('Session refresh coordination cancelled', {
                        attemptGeneration: generation,
                        sessionGeneration: this.#sessionGeneration,
                    });
                }
                return this.#state.value;
            }
            throw error;
        }
    }

    async #performRefresh(
        trigger: RefreshTrigger,
        sourceToken: string | null,
        generation: number,
        signal: AbortSignal,
    ): Promise<SessionState> {
        if (this.#isRefreshSuperseded(generation, signal)) {
            return this.#state.value;
        }

        if (!this.#oidcClient) {
            this.#log.error('OIDC client not initialized during refresh');
            return this.#setExpired('refresh-failed');
        }

        const currentState = this.#state.value;
        const claims =
            currentState.phase === 'authenticated' || currentState.phase === 'refreshing'
                ? currentState.claims
                : sourceToken
                  ? this.#decodeClaims(sourceToken)
                  : {};
        this.#state.value = { phase: 'refreshing', claims };

        this.#log.info('Session refresh started', { trigger });
        try {
            await this.#oidcClient.renewTokensAsync();
            if (this.#isRefreshSuperseded(generation, signal)) {
                this.#log.info('Ignoring refresh completed for a superseded session', {
                    attemptGeneration: generation,
                    sessionGeneration: this.#sessionGeneration,
                    trigger,
                });
                return this.#state.value;
            }

            const tokens = this.#oidcClient.tokens;
            if (!tokens?.accessToken) {
                this.#log.warn('Session refresh completed without an access token', { trigger });
                return this.#setExpired('refresh-failed');
            }

            if (trigger === 'server-rejected' && sourceToken === tokens.accessToken) {
                this.#log.warn('Session refresh returned the token rejected by the backend');
                return this.#setExpired('server-rejected');
            }

            this.#setAuthenticated({
                accessToken: tokens.accessToken,
                idToken: tokens.idToken,
                refreshToken: tokens.refreshToken,
            });
            if (trigger === 'server-rejected') {
                this.#replacementAwaitingValidation = tokens.accessToken;
            }

            this.#log.info('Session refresh succeeded', { trigger });

            // Compatibility signal for tabs still consuming the legacy storage event.
            this.#signalCrossTabSessionRestoration();

            return this.#state.value;
        } catch (error) {
            if (this.#isRefreshSuperseded(generation, signal)) {
                return this.#state.value;
            }
            this.#log.warn('Session refresh failed', { error, trigger });
            return this.#setExpired('refresh-failed');
        }
    }

    async ensureValidSession(): Promise<EnsureValidSessionResult> {
        if (TRACE) {
            this.#log.trace('ensureValidSession: Checking session validity on app load');
        }

        if (this.#state.value.phase === 'expired' && this.#state.value.reason === 'logout') {
            return { isValid: false };
        }

        if (!this.#oidcClient) {
            this.#log.error('ensureValidSession: OIDC client not initialized');
            this.#setExpired('refresh-failed');
            return { isValid: false };
        }

        const generation = this.#sessionGeneration;
        this.#state.value = { phase: 'authenticating' };
        try {
            // First, try to keep an existing session (checks if we have valid stored tokens)
            const hasExistingSession = await this.#oidcClient.tryKeepExistingSessionAsync();
            if (generation !== this.#sessionGeneration) {
                return { isValid: false };
            }

            if (TRACE) {
                this.#log.trace('ensureValidSession: tryKeepExistingSessionAsync result', { hasExistingSession });
            }

            if (hasExistingSession) {
                const tokens = this.#oidcClient.tokens;

                // tryKeepExistingSessionAsync returns true whenever tokens exist in storage,
                // even if they are expired. Verify the access token's JWT `exp` claim before
                // emitting TOKEN_REFRESHED — otherwise downstream consumers (e.g. userInfo
                // check in setAccessTokenSaga) will use an expired token, get a 401, and
                // trigger a spurious logout cascade.
                if (!tokens?.accessToken) {
                    this.#log.info('ensureValidSession: Stored session did not contain an access token');
                    // Fall through to the renewal path below.
                } else if (this.#isAccessTokenExpired(tokens.accessToken)) {
                    this.#log.info(
                        'ensureValidSession: Stored access token is expired, attempting renewal instead of restoring',
                    );
                    // Fall through to the renewal path below
                } else {
                    const restoredTokens = {
                        accessToken: tokens.accessToken,
                        idToken: tokens.idToken,
                        refreshToken: tokens.refreshToken,
                    };
                    const result: EnsureValidSessionResult = {
                        isValid: true,
                        tokens: restoredTokens,
                    };

                    this.#log.info('ensureValidSession: Existing session restored successfully');
                    this.#setAuthenticated(restoredTokens);

                    return result;
                }
            }

            // No valid existing session (or stored tokens were expired), try to refresh
            if (TRACE) {
                this.#log.trace('ensureValidSession: No existing session, attempting refresh');
            }

            const state = await this.#refresh('bootstrap');
            const tokens = this.#oidcClient.tokens;
            if (state.phase === 'authenticated' && tokens?.accessToken) {
                return {
                    isValid: true,
                    tokens: {
                        accessToken: tokens.accessToken,
                        idToken: tokens.idToken,
                        refreshToken: tokens.refreshToken,
                    },
                };
            }
            return { isValid: false };
        } catch (error) {
            if (generation !== this.#sessionGeneration) {
                return { isValid: false };
            }
            this.#log.error('ensureValidSession: Unexpected error', { error });
            this.#setExpired('refresh-failed');
            return { isValid: false };
        }
    }

    subscribeToAuthEvents(callback: AuthEventCallback): () => void {
        return this.#authEventBus.subscribe(callback);
    }
}
